import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faPlus, faEdit, faCheckCircle, faTimesCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import Layout from '../components/Layout';
import botService from '../services/botService';
import RefreshButton from '../components/RefreshButton';
import MoonLoader from "react-spinners/MoonLoader";
import { AuthContext } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

import './BotList.css';

const BotList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAdmin, isSuperAdmin } = useContext(AuthContext);

  useEffect(() => {
    loadBots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBots = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await botService.getAllBots();
      setBots(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar bots');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBot = () => {
    navigate('/create-bot');
  };

  const handleSelectBot = (botId) => {
    localStorage.setItem('selectedBotId', botId);
    navigate(`/manage-bot/${botId}`);
  };

  const handleDeleteBot = async (e, botId, botName) => {
    e.stopPropagation();
    if (!window.confirm(`Tem certeza que deseja excluir o bot "${botName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await botService.deleteBot(botId);
      setBots(bots.filter(b => b.id !== botId));
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir bot');
    }
  };


  if (loading) {
    return (
      <Layout>
        <div className="bot-list-page">
          <div className="dashboard-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
            <MoonLoader color="#487fff" size={40} />
            <p style={{ marginTop: '16px', color: '#6b7280' }}>{t('bot_list.loading', 'Carregando bots...')}</p>
          </div>
        </div>

      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bot-list-page">
        <div className="bot-list-header">
          
          <div className="header-actions">
            <RefreshButton onRefresh={loadBots} loading={loading} />
            <button onClick={handleCreateBot} className="btn btn-primary btn-create">
              <FontAwesomeIcon icon={faPlus} />
              {t('bot_list.create_new_bot', 'Criar novo bot')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {bots.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FontAwesomeIcon icon={faRobot} />
            </div>
            {isAdmin || isSuperAdmin ? (
              <>
                <h2>{t('bot_list.no_bots_found', 'Nenhum bot encontrado no sistema')}</h2>
                <p>{t('bot_list.create_to_start', 'Crie um novo bot para começar a gerenciar.')}</p>
              </>
            ) : (
              <>
                <h2>{t('bot_list.no_bots_created', 'Nenhum bot criado por você')}</h2>
                <p>{t('bot_list.start_creating', 'Comece criando seu primeiro bot do Telegram')}</p>
              </>
            )}
            <button onClick={handleCreateBot} className="btn btn-primary">
              <FontAwesomeIcon icon={faPlus} />
              {t('bot_list.create_first_bot', 'Criar primeiro bot')}
            </button>
          </div>
        ) : (
          <div className="bots-cards-container">
            {bots.map((bot) => (
              <div
                key={bot.id}
                className="bot-card"
                onClick={() => handleSelectBot(bot.id)}
              >
                <div className="bot-card-header">
                  <div className="bot-card-icon">
                    <FontAwesomeIcon icon={faRobot} />
                  </div>
                  <div className="bot-card-status">
                    {bot.active ? (
                      <span className="status-badge status-active">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        {t('common.active', 'Ativo')}
                      </span>
                    ) : (
                      <span className="status-badge status-inactive">
                        <FontAwesomeIcon icon={faTimesCircle} />
                        {t('common.inactive', 'Inativo')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="bot-card-body">
                  <h3 className="bot-card-name">{bot.name}</h3>
                  {bot.telegram_group_id && (
                    <p className="bot-card-group">
                      {t('bot_list.group', 'Grupo')}: {bot.telegram_group_id}
                    </p>
                  )}
                  <div className="bot-card-info">
                    <div className="info-item">
                      <span className="info-label">{t('common.status', 'Status')}:</span>
                      <span className={`info-value ${bot.activated ? 'activated' : 'not-activated'}`}>
                        {bot.activated ? t('bot_list.initialized', 'Inicializado') : t('bot_list.not_initialized', 'Não inicializado')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bot-card-footer">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectBot(bot.id);
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    {t('bot_list.manage', 'Gerenciar')}
                  </button>
                  <button
                    className="btn btn-danger btn-sm btn-delete-bot"
                    onClick={(e) => handleDeleteBot(e, bot.id, bot.name)}
                    title={t('bot_list.delete_bot', 'Excluir bot')}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BotList;

